import React, { useEffect, useState } from 'react';
import { CatalogOptions, CatalogParams } from './Catalog';
import { CatalogEvent, DeliveryMethod, EventGroup, SetLoading, TicketType } from '@arema/components/Classes';
import { Image, Icon } from 'react-frontier2';
import { CDN_URL, PLACEHOLDER_IMG } from '../AdminConfig';
import { addCommas } from '@arema/components/Util';
import { TicketTemplate } from '../AdminClasses';
import { SystemAccessRow } from '../components';
import { TERMINAL_TYPES } from '../AdminUtil';
import { t } from 'i18next';
import API, { UserLogin } from '../API';
import moment from 'moment';
import UserAccess from '../UserAccess';

type UserLoginUser = UserLogin["user"];

type HasAccessFn = (a: number | number[], use_or?: boolean, ignore_super?: boolean)=>boolean;
type CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, ...args: any[])=>CatalogOptions;

var formatUnix = (a: number)=> a ? moment.unix(a).format('DD/MM/YY')  : '';
var formatTime = (a: number)=> a ? moment.unix(a).format('DD/MM/YY HH:mm:ss') : '';
var formatTimeEvent = (a: number)=> a ? moment.unix(a).format('DD/MM/YY HH:mm:ss') : <Icon name="minus" color="grey" />;
var formatNull = (a: number)=> a && a!==0 ? `${a}` : <Icon name="minus" color="grey" />;

const EVENTS : CatalogType = ()=>({
	key: 'events',
	title: 'Eventos',
	emptyText: 'No hay eventos recientes en el sistema',
	catalogUrl: e=>`/events/${e.event_id}`,
	search: true,
	// createText: 'Crear evento',
	createUrl: '/events/create',
	pageSize: 20,
	pagination: true,
	getData: (params: CatalogParams, offset: number, count: number)=>{
		if(params && params.query){
			return API.searchEvent(params.query, offset, count);
		}else{
			return API.getPopularEvents();
		}
	},
	renderData: (a: CatalogEvent)=>{
		return <>
			<Image src={`${CDN_URL}/events/${a.event_id}/200.webp`} alt={a.event_name} fallback={PLACEHOLDER_IMG} />
			<div className="left">
				<div className="meta" style={{ fontSize: 12, lineHeight: '14px' }}>Evento #{a.event_id}</div>
				<div className="name">{a.event_name}</div>
				{!!a.date && <>
					<div className="date" style={{ fontSize: 12, lineHeight: '14px' }}>{moment.unix(a.date).format('DD/MM/YY')}</div>
					<div className="city" style={{ fontSize: 12, lineHeight: '14px' }}>{a.city}</div>
				</>}
			</div>
		</>
	},
	extractKey: (item: CatalogEvent)=>{
		return item.event_id;
	},
	access: {
		create: UserAccess.EVENTS.CREATE_EVENT,
	}
})

const VENUE : CatalogType = (user: UserLoginUser)=>({
	key: 'venue',
	title: 'Recintos',
	emptyText: 'No se encontraron recintos en el sistema',
	search: true,
	catalogUrl: u=>`/venues/${u.venue_id}`,
	// createText: 'Nuevo recinto',
	createUrl: `/venues/create`,
	pagination: true,
	pageSize: 20,
	table: [
		{ name: 'ID', key: 'venue_id', collapsing: true },
		{ name: 'Nombre', key: 'venue_name' },
		{ name: 'Ciudad', key: 'city', collapsing: true }
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getAllVenues(params?.query, offset, count);
	},
	access: {
		create: UserAccess.VENUES.CREATE_VENUE,
	}
});

const DELIVERY : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn)=>({
	key: 'delivery',
	title: 'Formas de Envio',
	emptyText: 'No existen formas de envio',
	catalogUrl: u=>`/c/delivery/${u.delivery_id}`,
	// createText: 'Nueva Forma de Envio',
	search: true,
	createUrl: `/c/delivery/create`,
	pageSize: 20,
	table: [
		{ name: 'ID', key: 'delivery_id', collapsing: true },
		{ name: 'Envío', key: 'method_name' },
		{ name: 'Tipo', key: 'ticket_type', collapsing: true, render: (v: number, obj: DeliveryMethod)=>{
			return <>
				<Icon name={obj.needs_location ? 'shipping-fast' : 'envelope'} style={{ marginRight: 10 }} />
				{(()=>{
					switch(v){
						case TicketType.DIGITAL: return 'Digital';
						case TicketType.PHYSICAL: return 'Impreso';
						default: return '???';
					}
				})()}
			</>
		}},
		{ name: 'Precio', key: 'delivery_cost', collapsing: true, render: v=>`$${addCommas(v)}` },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getDeliveryMethods(params?.query, offset, count);
	},
	access: {
		create: UserAccess.CATALOGS.DELIVERY_METHODS_CREATE,
	},
});

const PROMOTER : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn)=>({
	key: 'promoter',
	title: 'Promotores',
	emptyText: 'No existen promotores',
	catalogUrl: u=>`/promoters/${u.promoter_id}`,
	createModalTitle: 'Nuevo promotor',
	search: true,
	pagination: true,
	pageSize: 20,
	table: [
		{ name: 'ID', key: 'promoter_id', collapsing: true },
		{ name: 'Nombre', key: 'promoter_name', collapsing: false },
		{ name: 'Creación', key: 'date_created', collapsing: true, render: formatUnix },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getPromoters(params?.query, offset, count);
	},
	submitCreate: (data: any, setLoading: SetLoading)=>{
		return API.createPromoter(data.promoter_name);
	},
	form: {
		promoter_name: {
			type: 'text',
			label: 'Nombre de promotor',
			rules: [
				{ rule: 'length', params: [3,128], prompt: 'El nombre del promotor no es válido.' }
			]
		},
		msg: {
			type: 'message',
			content: 'Favor de asegurarse de que el promotor no exista antes de crearlo.'
		}
	},
	access: {
		create: UserAccess.CATALOGS.PROMOTER_CREATE,
	},
});

const PDV : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn)=>({
	key: 'pdv',
	title: 'Puntos de Venta',
	emptyText: 'No existen Puntos de Venta',
	catalogUrl: u=>`/pdv/${u.pdv_id}`,
	createUrl: `/pdv/create`,
	// createText: 'Nuevo PDV',
	search: true,
	pagination: true,
	pageSize: 20,
	table: [
		{ name: 'ID', key: 'pdv_id', collapsing: true },
		{ name: 'Nombre', key: 'pdv_name', collapsing: false },
		{ name: 'Ciudad', key: 'city', collapsing: true },
		{ name: 'Creación', key: 'date_created', collapsing: true, render: formatUnix },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getPDVList(params?.query, offset, count);
	},
	access: {
		create: UserAccess.PDV.CREATE_PDV,
	},
});

const PDV_ACCESS : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, pdv_id: number)=>({
	key: 'pdv-acc',
	title: 'Accesos',
	emptyText: 'No hay accesos en este PDV',
	pageSize: 20,
	table: [
		{ name: 'ID', key: 'login_id', collapsing: true },
		{ name: 'Usuario', key: 'user_id', collapsing: true, centered: true },
		{ name: 'Correo', key: 'email', collapsing: false },
		{ name: 'Fecha', key: 'date_login', collapsing: true, render: formatTime },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getPDVAcces(pdv_id, offset, count);
	},
});

const PDV_CORTES : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, pdv_id: number)=>({
	key: 'pdv-cortes',
	title: 'Cortes',
	emptyText: 'No hay cortes en este PDV',
	pageSize: 20,
	table: [
		{ name: 'ID', key: 'corte_id', collapsing: true },
		{ name: 'Nombre Inicio', key: 'name_start', collapsing: true },
		{ name: 'Fecha Inicio', key: 'date_start', collapsing: true, render: formatTime },
		{ name: 'Nombre Fin', key: 'name_end', collapsing: true},
		{ name: 'Fecha Fin', key: 'date_end', collapsing: true, render: formatTime },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getPDVCortes(pdv_id, offset, count);
	},
});

var getLoginType = (a: number)=>SystemAccessRow.Locale[a];

const USER_LOGINS : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, admin_id: number)=>({
	key: 'pdv-user',
	title: 'Accesos',
	emptyText: 'El usuario no tiene accesos',
	pageSize: 20,
	pagination: true,
	useTitle: false,
	table: [
		{ name: 'ID', key: 'login_id', collapsing: true },
		{ name: 'Tipo', key: 'source_id', collapsing: true, render: getLoginType },
		{ name: 'Nombre', key: 'pdv_name', collapsing: false, render: (a, v)=>(a || getLoginType(v.source_id)) },
		{ name: 'Fecha', key: 'date_login', collapsing: true, render: formatTime },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getUserLogins(admin_id, offset, count);
	},
});

const SCANNER : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn)=>({
	key: 'scanner',
	title: 'Checadores',
	emptyText: 'No hay checadores en el sistema',
	catalogUrl: c=>`/scanners/${c.scanner_id}`,
	// createText: 'Crear checador',
	createUrl: '/scanners/create',
	pageSize: 10,
	pagination: true,
	table: [
		{ name: 'ID', key: 'scanner_id', collapsing: true },
		{ name: 'Nombre', key: 'scanner_name', collapsing: false },
		{ name: 'Creación', key: 'date_created', collapsing: true, render: formatUnix },
	],
	search: true,
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getScannerList(params?.query, offset, count);
	},
	access: {
		create: UserAccess.CATALOGS.SCANNER_CREATE,
	},
});

const COUPON : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, event_id: number)=>({
	key: 'coupon',
	title: 'Cupones',
	emptyText: 'El evento no tiene cupones',
	createUrl: `/events/${event_id}/promotions/coupon/create`,
	catalogUrl: u =>`/events/${event_id}/promotions/coupon/${u.coupon_id}`,
	pageSize: 20,
	pagination: true,
	search: false,
	table: [
		{ name: 'ID', key: 'coupon_id', collapsing: true },
		{ name: 'Codigo', key: 'coupon_code', collapsing: true },
		{ name: 'Tipo', key: 'type_id', render: (v, o)=>t(`coupon_types.type_${v}`, { param1: o.param1, param2: o.param2 }), collapsing: true },
		{ name: 'Descripcion', key: 'description', collapsing: false },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getCouponList(event_id, params?.query, 0, 99999);
	},
	access: {
		create: UserAccess.CATALOGS.COUPONS_CREATE,
	},
});

const TICKET_TEMPLATE : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, admin_id: number)=>({
	key: 'ticket_template',
	title: 'Templates boletos',
	emptyText: 'No hay templates en el sistema',
	createUrl: '/templates/creator',
	// createText: 'Crear template',
	catalogUrl: u=>`/templates/${u.template_id}`,
	pageSize: 20,
	pagination: true,
	search: true,
	renderData: (a: TicketTemplate)=>{
		return <>
			{/* <Image src={`${CDN_URL}/events/${a.event_id}/200.webp`} alt={a.event_name} fallback={PLACEHOLDER_IMG} /> */}
			<div className="left">
				<div className="meta" style={{ fontSize: 12, lineHeight: '14px' }}>Template #{a.template_id}</div>
				<div className="name">{a.template_name}</div>
			</div>
		</>
	},
	extractKey: (i: TicketTemplate)=>{
		return i.template_id;
	},
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getTicketTemplates(params?.query, offset, count);
	},
})

const EVENT_GROUPS : CatalogType = ()=>({
	key: 'eventsgroups',
	title: 'Eventos Padres',
	emptyText: 'No hay eventos en el sistema',
	catalogUrl: e=>`/parent/${e.group_id}`,
	search: true,
	// createText: 'Crear evento',
	createUrl: '/parent/create',
	pageSize: 20,
	pagination: true,
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getEventGroups(params?.query, offset, count);
	},
	renderData: (a: EventGroup)=>{
		return <>
			<Image src={`${CDN_URL}/groups/${a.group_id}/200.webp`} alt={a.group_name} fallback={PLACEHOLDER_IMG} />
			<div className="left">
				<div className="meta" style={{ fontSize: 12, lineHeight: '14px' }}>Evento #{a.group_id}</div>
				<div className="name">{a.group_name}</div>
			</div>
		</>
	},
	extractKey: (item: EventGroup)=>{
		return item.group_id;
	},
	access: {
		create: UserAccess.EVENTS.CREATE_EVENT_GROUP,
	},
})

// const INVOICE: CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, admin_id: number)=>({
// 	key: 'partners',
// 	title: 'Facturas',
// 	emptyText: 'No hay facturas en el sistema',
// 	pageSize: 20,
// 	pagination: true,
// 	alwaysShowFilters: true,
// 	search: true,
// 	filters:[
// 		[
// 			{ key:'date_start', type: 'calendar', label: 'Fecha Inicio', shorthand: 'start', defaultValue: moment().add(-7, 'd').unix() }, 
// 			{ key:'date_end', type: 'calendar', label: 'Fecha Fin', shorthand: 'end', defaultValue: moment().unix() }
// 		]
// 	],
// 	table: [
// 		{ name: 'ID', key: 'invoice_id', collapsing: true },
// 		{ name: 'Nombre', key: 'legal_name', collapsing: true },
// 		{ name: 'RFC', key: 'rfc', collapsing: true },
// 		{ name: 'Cantidad', key: 'amount', collapsing: true, centered: true, render: addCommas },
// 	],
// 	getData: (params: CatalogParams, offset: number, count: number)=>{
// 		console.log(params);

// 		return API.getInvoices(params.date_start, params.date_end, 0, 30);
// 	},
// });

const EVENT_ORDERS_CONCILIA: CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, admin_id: number, event_id: number, method_id: number)=>({
	key: 'partners',
	title: 'Ordenes',
	emptyText: 'No hay ordenes',
	pageSize: 20,
	pagination: true,
	search: true,
	table: [
		{ name: 'Folio', key: 'order_id', collapsing: true },
		{ name: 'Estado', key: 'estado', collapsing: true },
		{ name: 'PDV', key: 'pdv_name', collapsing: true },
		{ name: 'Fecha', key: 'date', collapsing: true },
		{ name: 'Metodo', key: 'method_name', collapsing: true },
		{ name: 'Total', key: 'total', collapsing: true, render: addCommas },
		{ name: 'Conciliado', key: 'total_deposit', render: addCommas },
		{ name: 'Completado', key: 'completed', collapsing: true },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getEventOrdersConcilia(event_id, method_id, params?.query, offset, count);
	},
});

const PROMOTER_EVENTS: CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, promoter_id: number,)=>({
	key: 'promoter_event',
	title: null,
	emptyText: 'El promotor no tiene eventos próximos',
	catalogUrl: u =>`/events/${u.event_id}`,
	pageSize: 20,
	pagination: true,
	search: true,
	table: [
		{ name: 'ID', key: 'event_id', collapsing: true },
		{ name: 'Evento', key: 'event_name' },
		{ name: 'Proxima Fecha', key: 'date', collapsing: true, render: formatTimeEvent },
		{ name: 'Recinto', key: 'venue_name', collapsing: true, render: formatNull },
		{ name: 'Ciudad', key: 'city', collapsing: true, render: formatNull  },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getPromoterEvents(promoter_id, params?.query, offset, count);
	},
});

const EVENT_DATE_QUESTION: CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn, event_id: number, date_id: number)=>({
	key: 'event_date_question',
	title: `Preguntas Calendario [${date_id}]`,
	emptyText: 'No hay preguntas en este evento',
	catalogUrl: u => `/events/${event_id}/forms/${date_id}/q/${u.question_id}`,
	pageSize: 20,
	pagination: true,
	// createText: 'Crear pregunta',
	createUrl: `/events/${event_id}/forms/${date_id}/q/create`,
	search: true,
	table: [
		{ name: 'ID', key: 'question_id', collapsing: true },
		{ name: 'Question', key: 'question' },
		{ name: 'Comentario', key: 'comment' },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getEventDateQuestions(date_id);
	},
});

const ZONES : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn)=>({
	key: 'zones',
	title: 'Zonas',
	emptyText: 'No hay zonas en el sistema',
	catalogUrl: u =>`/zones/${u.zone_id}`,
	pageSize: 20,
	pagination: true,
	search: true,
	table: [
		{ name: 'ID', key: 'zone_id', collapsing: true },
		{ name: 'Nombre', key: 'zone_name', collapsing: true },
		{ name: 'Descripción', key: 'description' },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getZonesList(params?.query, offset, count);
	},
	submitCreate: (data: any, setLoading: SetLoading)=>{
		return API.createZone(data.zone_name, data.description);
	},
	form: {
		zone_name: {
			type: 'text',
			label: 'Nombre de zona',
			rules: [
				{ rule: 'length', params: [3,128], label: 'Nombre' }
			]
		},
		description: {
			type: 'text',
			label: 'Descripción',
			description: 'Descripción de la zona que ayudará a distingirla',
			rules: [
				{ rule: 'minLength', params: [3], label: 'Descripción' }
			]
		}
	},
	access: {
		create: UserAccess.CATALOGS.ZONES_CREATE,
	},
});

const BANK : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn)=>({
	key: 'bank',
	title: 'Bancos',
	emptyText: 'No hay bancos en el sistema',
	catalogUrl: u =>`/banks/${u.bank_id}`,
	pageSize: 50,
	pagination: true,
	search: true,
	table: [
		{ name: 'ID', key: 'bank_id', collapsing: true },
		{ name: 'Nombre', key: 'bank_name' },
		{ name: 'Creación', key: 'date_created', collapsing: true, render: formatUnix },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getBankBinList(params?.query, offset, count);
	},
	submitCreate: (data: any, setLoading: SetLoading)=>{
		return API.createBankBin(data.bank_name, data.description);
	},
	form: {
		bank_name: {
			type: 'text',
			label: 'Nombre de banco',
			rules: [
				{ rule: 'length', params: [3,128], label: 'Nombre' }
			]
		},
		description: {
			type: 'message',
			content: 'Favor de asegurarse de que no exista ya el banco que se desea crear, en caso de que exista, se debe de reutilizar.'
		}
	},
	access: {
		create: UserAccess.CATALOGS.BANK_CREATE,
	},
});

const TERMINALS : CatalogType = (user: UserLoginUser, hasAccess: HasAccessFn)=>({
	key: 'terminal',
	title: 'Terminales',
	emptyText: 'No hay terminales en el sistema',
	catalogUrl: u =>`/terminals/${u.terminal_id}`,
	pageSize: 20,
	pagination: true,
	search: true,
	table: [
		{ name: 'ID', key: 'terminal_id', collapsing: true },
		{ name: 'Nombre', key: 'terminal_name' },
		{ name: 'Creación', key: 'date_created', collapsing: true, render: formatUnix },
	],
	getData: (params: CatalogParams, offset: number, count: number)=>{
		return API.getPaymentTerminalList(params?.query, offset, count);
	},
	submitCreate: (data: any)=>{
		return API.createPaymentTerminal(data.terminal_name, data.terminal_type, data.license_id);
	},
	form: {
		terminal_name: {
			type: 'text',
			label: 'Nombre identificador',
			description: 'Nombre que identifica la terminal bancaria',
			rules: [
				{ rule: 'length', params: [3,64], label: 'Nombre' }
			]
		},
		terminal_type: {
			label: 'Tipo de terminal',
			type: 'dropdown',
			options: TERMINAL_TYPES.map(a=>({
				value: a.id,
				text: a.name
			})),
		},
		license_id: {
			label: 'Licencia',
			type: 'dropdown',
			fetchOptions: async ()=>{
				try{
					var res = await API.getLicenses();
					if(res.error) return [];
					return res.data.map(a=>({
						value: a.license_id,
						text: a.license_name
					}));
				}catch(e){
					return [];
				}
			}
		}
	},
	access: {
		create: UserAccess.CATALOGS.TERMINALS_CREATE,
	},
});

export {
	COUPON,
	DELIVERY,
	EVENTS,
	USER_LOGINS,
	PDV_ACCESS,
	PDV_CORTES,
	PDV,
	PROMOTER,
	SCANNER,
	TICKET_TEMPLATE,
	VENUE,
	EVENT_GROUPS,
	// INVOICE,
	EVENT_ORDERS_CONCILIA,
	PROMOTER_EVENTS,
	EVENT_DATE_QUESTION,
	ZONES,
	BANK,
	TERMINALS,
}